import { motion } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";
import { DISCORD_URL } from "@/lib/config";
import HeroFloatingSprites from "@/components/origin/HeroFloatingSprites";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate pt-28 pb-24 sm:pt-36 sm:pb-32 lg:pt-40 lg:pb-40 min-h-[88vh] flex items-center"
    >
      {/* Aurora glow */}
      <div className="aurora" />

      {/* Floating Growtopia sprites — interactive, drift on click */}
      <HeroFloatingSprites />

      <div className="relative z-20 mx-auto max-w-7xl px-6 lg:px-8 w-full">
        <div className="relative mx-auto max-w-3xl text-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-300 backdrop-blur-md"
            data-testid="hero-eyebrow"
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full bg-emerald-400 pulse-dot" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            Server online · Premium Growtopia experience
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-display mt-6 text-[2.6rem] sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95]"
            data-testid="hero-title"
          >
            <span className="text-gradient-white">The ultimate</span>
            <br />
            <span className="text-gradient-gold">Growtopia</span>{" "}
            <span className="text-gradient-white">experience.</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mx-auto mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-400 px-2"
            data-testid="hero-subtitle"
          >
            OriginPS is a premium private server with custom items, an active
            economy and zero lag. Join the elite community and play the way it
            was meant to be.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-join-discord-btn"
              className="glossy-gold inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-transform hover:scale-[1.03] active:scale-[0.98] w-full sm:w-auto"
            >
              <Zap className="h-4 w-4" />
              Join Discord
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#how-to-play"
              data-testid="hero-how-to-play-btn"
              className="ghost-pill inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium transition-all w-full sm:w-auto"
            >
              How to play
            </a>
          </motion.div>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-6 text-[11px] uppercase tracking-[0.22em] text-slate-500"
          >
            Tip · tap a floating item, they drift away then come back
          </motion.p>
        </div>
      </div>
    </section>
  );
}
